import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Tabs } from '@/components/ui/Tabs';
import { useScrollReveal } from '@/lib/useScrollReveal';
import clsx from 'clsx';

const claudeCode = `// Claude Agent Memory Context
{
  "agent": "claude-sonnet-4-20250514",
  "recent_memories": [
    {
      "type": "task_progress",
      "content": "Completed Phase 2 of DB migration",
      "score": 0.94
    },
    {
      "type": "conversation",
      "content": "User asked to refactor auth module",
      "score": 0.87
    }
  ],
  "context_window": "47% used"
}`;

const gptCode = `// GPT Agent Memory Context
{
  "agent": "gpt-4o",
  "recent_memories": [
    {
      "type": "task_progress",
      "content": "Completed Phase 2 of DB migration",
      "score": 0.94
    },
    {
      "type": "tool_result",
      "content": "Auth module refactored successfully",
      "score": 0.91
    }
  ],
  "context_window": "52% used"
}`;

export const CrossProviderDemo: React.FC = () => {
  const [isTransferring, setIsTransferring] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  const handleTransfer = () => {
    if (isTransferring) return;
    setIsTransferring(true);
    setShowStats(false);

    setTimeout(() => {
      setIsTransferring(false);
      setShowStats(true);
    }, 1500);
  };

  return (
    <section className="py-20 bg-[#EEF2FF]" id="api" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={clsx(
            'text-center mb-12 transition-all duration-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            Cross-Provider Memory Transfer
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Your agents remember everything — even when switching from Claude to GPT
          </p>
        </div>

        <div
          className={clsx(
            'transition-all duration-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
          style={{ transitionDelay: '150ms' }}
        >
          <Tabs
            tabs={[
              {
                label: 'Claude Agent',
                panel: (
                  <div
                    className={clsx(
                      'bg-code-bg rounded-xl p-4 overflow-hidden transition-all duration-500',
                      isTransferring && 'opacity-50 scale-95'
                    )}
                  >
                    <pre className="text-sm font-mono text-slate-300 overflow-x-auto">
                      {claudeCode}
                    </pre>
                  </div>
                ),
              },
              {
                label: 'GPT Agent',
                panel: (
                  <div
                    className={clsx(
                      'bg-code-bg rounded-xl p-4 overflow-hidden transition-all duration-500',
                      isTransferring && 'opacity-50 scale-95'
                    )}
                  >
                    <pre className="text-sm font-mono text-slate-300 overflow-x-auto">
                      {gptCode}
                    </pre>
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* Transfer button and connector */}
        <div
          className={clsx(
            'flex flex-col items-center my-8 transition-all duration-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
          style={{ transitionDelay: '250ms' }}
        >
          <button
            onClick={handleTransfer}
            disabled={isTransferring}
            className={clsx(
              'flex items-center gap-2 px-6 py-3 bg-secondary hover:bg-secondary-dark text-white font-medium rounded-lg transition-all duration-200',
              isTransferring && 'animate-pulse'
            )}
          >
            Transfer Memory
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* Animated connector line */}
          <div className="relative w-full max-w-md h-1 mt-4">
            <div
              className={clsx(
                'absolute inset-0 bg-gradient-to-r from-indigo-500 via-emerald-500 to-amber-500 rounded-full transition-all duration-1000',
                isTransferring ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
              )}
              style={{ transformOrigin: 'center' }}
            />
            {isTransferring && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full animate-ping" />
            )}
          </div>
        </div>

        {/* Stats display */}
        <div
          className={clsx(
            'grid md:grid-cols-2 gap-6 max-w-2xl mx-auto transition-all duration-500',
            showStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
        >
          <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-border">
            <div className="text-4xl font-bold font-mono text-primary mb-2">98%</div>
            <div className="text-text-secondary">Context continuity</div>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-border">
            <div className="text-4xl font-bold font-mono text-secondary mb-2">47</div>
            <div className="text-text-secondary">Memories transferred</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CrossProviderDemo;